import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ArrowRight, Box, Ruler, Info } from 'lucide-react';
import useBoxTypes from '../../hooks/useBoxTypes';
import BrutalistContainer from '../ui/BrutalistContainer';
import AvailabilityMatrix from './AvailabilityMatrix';

const BrutalistCatalog = () => {
  const [filter, setFilter] = useState('all');
  const containerRef = useRef(null);
  const { boxes: storageUnits } = useBoxTypes();

  const filters = [
    { id: 'all', label: 'Все боксы' },
    { id: 'small', label: 'Малые (1-3 м²)' },
    { id: 'medium', label: 'Средние (4-8 м²)' },
    { id: 'large', label: 'Большие (9+ м²)' },
  ];

  const getCategory = (sizeStr) => {
    const size = parseInt(sizeStr);
    if (size <= 3) return 'small';
    if (size <= 8) return 'medium';
    return 'large';
  };

  const filteredUnits = storageUnits.filter(unit => {
    if (filter === 'all') return true;
    return getCategory(unit.size) === filter;
  });

  // Magnetic hover effect
  const handleMouseEnter = (e) => {
    gsap.to(e.currentTarget, {
      scale: 1.03,
      y: -4,
      duration: 0.3,
      ease: 'power2.out',
      boxShadow: '8px 8px 0px 0px rgba(0,0,0,1)'
    });
  };

  const handleMouseLeave = (e) => {
    gsap.to(e.currentTarget, {
      scale: 1,
      y: 0,
      duration: 0.3,
      ease: 'power2.out',
      boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)' // Default shadow
    });
  };

  // Анимация появления карточек. fromTo + clearProps — критично:
  // 1) не скроллим при смене фильтра (раньше был window.scrollTo)
  // 2) явное конечное состояние opacity:1 — иначе при прерывании tween
  //    карточка застревала на промежуточной opacity (~0.11), выглядела «выцветшей»
  // 3) clearProps убирает inline-стили после анимации — дальше работают обычные CSS-классы
  // 4) overwrite:'auto' гасит предыдущие tween'ы на тех же элементах
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.catalog-card',
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.08,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'opacity,transform,translate,rotate,scale',
        }
      );
    }, containerRef);
    return () => ctx.revert();
  }, [filter]);

  return (
    <section ref={containerRef} className="py-32 px-6 min-h-screen bg-background text-foreground relative">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-16">
          <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground uppercase tracking-widest mb-4">
            <Link to="/" className="hover:text-accent transition-colors">[ГЛАВНАЯ]</Link>
            <span>/</span>
            <span>[КАТАЛОГ]</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-sans font-bold uppercase tracking-tighter mb-6">
            Каталог <span className="text-accent">Боксов</span>
          </h1>
          <p className="max-w-2xl text-xl font-mono text-muted-foreground">
            Выберите оптимальный размер. Доступ 24/7. Без скрытых платежей.
          </p>
        </div>

        {/* Availability table — live data from backend */}
        <AvailabilityMatrix />

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-12">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`
                px-6 py-3 rounded-full font-mono text-sm uppercase tracking-wider border transition-all duration-300
                ${filter === f.id 
                  ? 'bg-foreground text-background border-foreground' 
                  : 'bg-transparent text-foreground border-border hover:border-foreground'
                }
              `}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
          {filteredUnits.map((unit) => (
            <div
              key={unit.id}
              className={`catalog-card relative bg-card border transition-all duration-300 group flex flex-col h-full
                ${unit.available
                  ? 'border-border hover:border-accent'
                  : 'border-border opacity-60 pointer-events-none grayscale'
                }
              `}
              style={{ boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)' }}
              onMouseEnter={unit.available ? handleMouseEnter : undefined}
              onMouseLeave={unit.available ? handleMouseLeave : undefined}
            >
              {/* Status Badge */}
              <div className="absolute top-4 right-4 z-10">
                {unit.available ? (
                  <div className="px-3 py-1 bg-background border border-accent text-accent font-mono text-xs uppercase tracking-widest">
                    ● Свободно
                  </div>
                ) : (
                  <div className="px-3 py-1 bg-muted text-muted-foreground font-mono text-xs uppercase tracking-widest border border-muted-foreground">
                    Занято
                  </div>
                )}
              </div>

              {/* Image / Visual — без grayscale-по-умолчанию,
                  иначе hover на одной карточке делает её «цветной», а соседние выглядят как заблокированные */}
              <div className="h-48 bg-muted border-b border-border overflow-hidden relative transition-all duration-500">
                {unit.image ? (
                  <img 
                    src={unit.image} 
                    alt={unit.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <BrutalistContainer className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                )}
                 <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-mono text-4xl font-bold text-white drop-shadow-lg pointer-events-none select-none z-10">
                   {unit.name}
                 </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <h3 className="text-3xl font-sans font-bold uppercase">{unit.name}</h3>
                    <div className="font-mono text-xs text-muted-foreground uppercase mt-1">{unit.dimensions}</div>
                  </div>
                  <div className="text-right">
                    <a href="tel:+79184362255" className="text-2xl font-bold font-sans hover:text-accent transition-colors">Звоните</a>
                    <div className="font-mono text-xs text-muted-foreground">узнать цену</div>
                  </div>
                </div>

                <div className="space-y-3 mb-6 font-mono text-sm border-t border-border pt-4 flex-1">
                  <div className="flex items-center gap-3">
                    <Ruler className="w-4 h-4" />
                    <span>Площадь: <strong>{unit.size}</strong></span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Box className="w-4 h-4" />
                    <span>Объем: <strong>{unit.volume}</strong></span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Info className="w-4 h-4" />
                    <span className="truncate">{unit.description}</span>
                  </div>
                </div>

                <Link to={`/booking?type=${unit.name}`} className="w-full py-4 bg-foreground text-background font-mono uppercase tracking-widest flex items-center justify-center gap-2 group-hover:bg-accent group-hover:text-white transition-colors mt-auto">
                  Забронировать
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredUnits.length === 0 && (
          <div className="text-center py-20 border border-dashed border-border">
            <div className="font-mono text-muted-foreground uppercase tracking-widest">
              [СИСТЕМА: БОКСЫ НЕ НАЙДЕНЫ]
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default BrutalistCatalog;
